import {
  CHINA_MAP_LABEL_FONT_SIZE,
  type ChinaMap,
  type ChinaMapLabelLayout,
  type ChinaMapPlace,
  type ChinaMapProvince,
} from "@-/dev.improve.haniverse.data.china-map";
import {
  getChinaMapPlaceRings,
  isPointInChinaMapBbox,
  isPointInChinaMapRings,
} from "#china-map-geometry";
import {
  getChinaPlaceLessonsUntilUnlock,
  getChinaPlaceUnlock,
  type ChinaPlaceUnlock,
} from "#china-place-unlock";
import type { ChinaMapColors } from "#use-china-map-colors";
import { placeChinaMapCoastalLabels } from "#china-map-coastal-labels";
import { placeChinaMapViewportLabels } from "#place-china-map-viewport-labels";

export interface ChinaMapPlaceView {
  /** The short name in the user's script (`广东` / `廣東`). */
  displayName: string;
  /**
   * Lessons left until every character of the name is taught (`getChinaPlaceLessonsUntilUnlock`):
   * 0 once unlocked, `null` when unknown or some character is never taught.
   */
  lessonsUntilUnlock: number | null;
  place: ChinaMapPlace;
  /** The province-level region this place belongs to (itself, for a province). */
  province: ChinaMapProvince;
  unlock: ChinaPlaceUnlock;
}

export interface ChinaMapProvinceView extends ChinaMapPlaceView {
  children: readonly ChinaMapPlaceView[];
}

const NO_LESSON_COUNTS: ReadonlyMap<string, number> = new Map();

export function createChinaMapPlaceViews(
  chinaMap: ChinaMap,
  {
    hanziScript,
    labelHanziScript = hanziScript,
    lessonsUntilByCharacter = NO_LESSON_COUNTS,
    levelIndexByCharacter,
    studiedCharacters,
  }: {
    hanziScript: "simplified" | "traditional";
    /**
     * Script for map labels and place titles. Defaults to {@link hanziScript}. Regions that write
     * in traditional Chinese (Taiwan, Hong Kong, Macau) always pass `"traditional"`.
     */
    labelHanziScript?: "simplified" | "traditional";
    /** From `createLessonsUntilByCharacter`; without it no place knows its lessons left. */
    lessonsUntilByCharacter?: ReadonlyMap<string, number>;
    levelIndexByCharacter: ReadonlyMap<string, number>;
    studiedCharacters: ReadonlySet<string>;
  },
): ChinaMapProvinceView[] {
  const toView = (place: ChinaMapPlace, province: ChinaMapProvince): ChinaMapPlaceView => {
    const displayName =
      labelHanziScript === "traditional" ? place.traditionalShortName : place.shortName;
    // Unlock against the learner's curriculum script so simplified levels still match.
    const unlockName = hanziScript === "traditional" ? place.traditionalShortName : place.shortName;
    return {
      displayName,
      lessonsUntilUnlock: getChinaPlaceLessonsUntilUnlock(
        unlockName,
        studiedCharacters,
        lessonsUntilByCharacter,
      ),
      place,
      province,
      unlock: getChinaPlaceUnlock(
        displayName,
        levelIndexByCharacter,
        studiedCharacters,
        unlockName,
      ),
    };
  };
  return chinaMap.provinces.map((province) => ({
    ...toView(province, province),
    children: province.children.map((child) => toView(child, province)),
  }));
}

/** Finds a place (city, district, or childless province) by adcode. */
export function findChinaMapPlaceView(
  provinces: readonly ChinaMapProvinceView[],
  adcode: number,
): { province: ChinaMapProvinceView; view: ChinaMapPlaceView } | null {
  for (const province of provinces) {
    if (province.place.adcode === adcode) {
      return { province, view: province };
    }
    const child = province.children.find((candidate) => candidate.place.adcode === adcode);
    if (child !== undefined) {
      return { province, view: child };
    }
  }
  return null;
}

function containsPoint(place: ChinaMapPlace, x: number, y: number): boolean {
  return (
    isPointInChinaMapBbox(place.bbox, x, y) &&
    isPointInChinaMapRings(getChinaMapPlaceRings(place), x, y)
  );
}

/** The regions a province is drawn as: its cities (or districts), or itself when it has none. */
export function getChinaMapProvinceRegions(
  province: ChinaMapProvinceView,
): readonly ChinaMapPlaceView[] {
  return province.children.length > 0 ? province.children : [province];
}

/** Cities/districts are visible and selectable at every zoom; use a province only without children. */
export function getChinaMapVisiblePlaces(
  provinces: readonly ChinaMapProvinceView[],
): ChinaMapPlaceView[] {
  return provinces.flatMap((province) => getChinaMapProvinceRegions(province));
}

/**
 * Whether a province's land is shaded: its own name and at least one city's name must be learned.
 * A province without subdivisions is itself a destination and only needs its own name learned.
 */
export function isChinaMapProvinceShaded(province: ChinaMapProvinceView): boolean {
  return (
    province.unlock.status === "unlocked" &&
    getChinaMapProvinceRegions(province).some((region) => region.unlock.status === "unlocked")
  );
}

/** Selection mutes the other provinces' land and borders, preserving the focused province. */
export function getChinaMapProvinceColors(
  province: ChinaMapProvinceView,
  focusedProvince: ChinaMapProvinceView | null,
  colors: ChinaMapColors,
): Pick<ChinaMapColors, "cityBorder" | "land" | "provinceBorder" | "unlockedLand"> {
  if (focusedProvince === null || province.place.adcode === focusedProvince.place.adcode) {
    return colors;
  }
  return {
    cityBorder: colors.unfocusedBorder,
    land: colors.unfocusedLand,
    provinceBorder: colors.unfocusedBorder,
    unlockedLand: colors.unfocusedLand,
  };
}

/**
 * Whether one region of a province is shaded. Inside the province whose cities are named (see
 * `getChinaMapCityLabelProvince`), each city shows its own progress; everywhere else the whole
 * province shows the province's.
 */
export function isChinaMapRegionShaded(
  province: ChinaMapProvinceView,
  region: ChinaMapPlaceView,
  cityLabelProvince: ChinaMapProvinceView | null,
): boolean {
  return province.place.adcode === cityLabelProvince?.place.adcode
    ? region.unlock.status === "unlocked"
    : isChinaMapProvinceShaded(province);
}

function countUnstudiedCharacters(view: ChinaMapPlaceView): number {
  return view.unlock.characters.filter((character) => !character.studied).length;
}

/**
 * The places the learner will unlock soonest: locked provinces and, only within unlocked
 * provinces, locked cities (and districts). All places share the same ordering: the fewest lessons
 * left first (unknown counts last), then the earliest unlocking level, then the fewest characters
 * left to learn. Visited places and names with a character no level teaches are left out.
 */
export function getChinaMapUpcomingPlaces(
  provinces: readonly ChinaMapProvinceView[],
  visited: ReadonlySet<number>,
): ChinaMapPlaceView[] {
  const upcoming = provinces
    .flatMap((province) =>
      province.unlock.status === "unlocked" ? province.children : [province],
    )
    .filter(
      (view) =>
        view.unlock.status !== "unlocked" &&
        view.unlock.levelIndex !== null &&
        !visited.has(view.place.adcode),
    );
  return upcoming.sort(
    (a, b) =>
      (a.lessonsUntilUnlock ?? Infinity) - (b.lessonsUntilUnlock ?? Infinity) ||
      (a.unlock.levelIndex ?? 0) - (b.unlock.levelIndex ?? 0) ||
      countUnstudiedCharacters(a) - countUnstudiedCharacters(b),
  );
}

/**
 * How far, in screen px at the full font size, the text baseline sits below the middle of a
 * character. Chinese glyphs fill an em box from 0.88 em above the baseline to 0.12 em below it, so
 * this centers them on their spot.
 */
export const CHINA_MAP_LABEL_BASELINE_OFFSET = CHINA_MAP_LABEL_FONT_SIZE * 0.38;

/**
 * A province's name is never left out. Where even its roomiest layout is smaller than this, in
 * screen px (上海 at the whole-country view), the name is drawn at this size over its borders.
 */
export const CHINA_MAP_PROVINCE_LABEL_MIN_FONT_SIZE = 6;
/** A city's name is left out until it fits its outline at this size, in screen px. */
export const CHINA_MAP_CITY_LABEL_MIN_FONT_SIZE = 9;

/** A name to draw: how its characters are laid out around the layout's anchor, and how large. */
export interface ChinaMapLabel {
  /** In screen px: the full size wherever some layout fits at it. */
  fontSize: number;
  layout: ChinaMapLabelLayout;
  /** A water-side callout's line, from its region's boundary to just short of the text. */
  leader?: { from: readonly [number, number]; to: readonly [number, number] };
  view: ChinaMapPlaceView;
}

/**
 * How the name fits its outline at this zoom: at the full font size in the plainest layout that
 * holds it (across in a line where there is room, then ever freer arrangements of its characters).
 * Only where no arrangement holds it is the font shrunk, in the layout that holds it largest. The
 * layouts and their sizes are found when the map is built (`findChinaMapLabelLayouts`).
 */
export function getChinaMapLabel(view: ChinaMapPlaceView, scale: number): ChinaMapLabel | null {
  if (!Number.isFinite(scale) || scale <= 0) {
    return null;
  }
  const layouts = view.place.labels;
  const layout =
    layouts.find(({ em }) => em * scale >= CHINA_MAP_LABEL_FONT_SIZE) ?? layouts.at(-1);
  if (layout === undefined) {
    return null;
  }
  return { fontSize: Math.min(CHINA_MAP_LABEL_FONT_SIZE, layout.em * scale), layout, view };
}

/**
 * A name's characters, each with the middle of its glyph in ems from the label anchor. Split by
 * code point, like `getChinaPlaceUnlock`.
 */
export function getChinaMapLabelGlyphs(
  name: string,
  offsets: ChinaMapLabelLayout["offsets"],
): { text: string; x: number; y: number }[] {
  return Array.from(name, (text, index) => {
    const [x = 0, y = 0] = offsets[index] ?? [];
    return { text, x, y };
  });
}

/** Neutral map ink and font weight follow each glyph's curriculum progress, in label order. */
export function getChinaMapStyledLabelGlyphs(
  view: ChinaMapPlaceView,
  offsets: ChinaMapLabelLayout["offsets"],
  colors: Pick<ChinaMapColors, "label" | "lockedLabel">,
): { color: string; fontWeight: 400 | 600; text: string; x: number; y: number }[] {
  return getChinaMapLabelGlyphs(view.displayName, offsets).map((glyph, index) => {
    const studied = view.unlock.characters[index]?.studied === true;
    return {
      ...glyph,
      color: studied ? colors.label : colors.lockedLabel,
      fontWeight: studied ? 600 : 400,
    };
  });
}

/**
 * How much of the visible map one province must cover before it is auto-focused: its cities are
 * named as if it were selected, without selecting it. A majority, so at most one province
 * qualifies; irregular provinces like 四川 rarely pass 60% even when they visibly fill the screen.
 */
export const CHINA_MAP_AUTO_FOCUS_COVERAGE = 0.5;

/** Coverage is estimated on a grid of this many points per side over the visible map. */
const CHINA_MAP_COVERAGE_GRID = 12;

/**
 * The province covering most of the visible map (`[minX, minY, maxX, maxY]` in map units), if it
 * covers at least `CHINA_MAP_AUTO_FOCUS_COVERAGE` of it; otherwise `null`.
 */
export function findChinaMapAutoFocusedProvince(
  provinces: readonly ChinaMapProvinceView[],
  visibleBbox: readonly [number, number, number, number],
): ChinaMapProvinceView | null {
  const [minX, minY, maxX, maxY] = visibleBbox;
  if (!(maxX > minX && maxY > minY)) {
    return null;
  }
  const counts = new Map<ChinaMapProvinceView, number>();
  for (let column = 0; column < CHINA_MAP_COVERAGE_GRID; column += 1) {
    const x = minX + ((column + 0.5) / CHINA_MAP_COVERAGE_GRID) * (maxX - minX);
    for (let row = 0; row < CHINA_MAP_COVERAGE_GRID; row += 1) {
      const y = minY + ((row + 0.5) / CHINA_MAP_COVERAGE_GRID) * (maxY - minY);
      const province = provinces.find((candidate) => containsPoint(candidate.place, x, y));
      if (province !== undefined) {
        counts.set(province, (counts.get(province) ?? 0) + 1);
      }
    }
  }
  let best: ChinaMapProvinceView | null = null;
  let bestCount = 0;
  for (const [province, count] of counts) {
    if (count > bestCount) {
      best = province;
      bestCount = count;
    }
  }
  return bestCount >= CHINA_MAP_COVERAGE_GRID ** 2 * CHINA_MAP_AUTO_FOCUS_COVERAGE ? best : null;
}

/**
 * The one province whose cities are named: the province filling the screen (auto-focused) if any,
 * else the selected one. `null` names provinces instead. Auto-focus never changes the selection.
 */
export function getChinaMapCityLabelProvince({
  autoFocusedProvince,
  focusedProvince,
}: {
  autoFocusedProvince: ChinaMapProvinceView | null;
  focusedProvince: ChinaMapProvinceView | null;
}): ChinaMapProvinceView | null {
  return autoFocusedProvince ?? focusedProvince;
}

/**
 * The names to draw: either every province's, or the cities (districts) of one province, never a
 * mix. A province is always named, in a smaller font where its outline holds no layout at the
 * full size; a city is named once it fits.
 */
export function getChinaMapLabels(
  provinces: readonly ChinaMapProvinceView[],
  {
    cityLabelProvince,
    scale,
    visibleBbox,
  }: {
    cityLabelProvince: ChinaMapProvinceView | null;
    scale: number;
    visibleBbox?: readonly [number, number, number, number];
  },
): ChinaMapLabel[] {
  const views =
    cityLabelProvince === null ? provinces : getChinaMapProvinceRegions(cityLabelProvince);
  const labels = views.flatMap((view) => {
    let label = getChinaMapLabel(view, scale);
    if (label === null && (view.place.coast?.length ?? 0) > 0) {
      // A missing interior layout must still get a chance over water. This zero-size seed is
      // never drawn; the coast solver replaces it only when the full name fits safely.
      label = {
        fontSize: 0,
        layout: { anchor: view.place.centroid, em: 0, offsets: [] },
        view,
      };
    }
    return label === null ? [] : [label];
  });
  // Before callouts: a name its region's visible part holds only smaller can still get one.
  return placeChinaMapCoastalLabels(
    visibleBbox === undefined
      ? labels
      : placeChinaMapViewportLabels(labels, {
          minFontSize: CHINA_MAP_CITY_LABEL_MIN_FONT_SIZE,
          scale,
          visibleBbox,
        }),
    {
      alwaysLabel: cityLabelProvince === null,
      minFontSize:
        cityLabelProvince === null
          ? CHINA_MAP_PROVINCE_LABEL_MIN_FONT_SIZE
          : CHINA_MAP_CITY_LABEL_MIN_FONT_SIZE,
      provinces,
      scale,
      visibleBbox,
    },
  );
}

/** Resolve the city under a tap, regardless of the currently focused province. */
export function findChinaMapPlaceAt(
  provinces: readonly ChinaMapProvinceView[],
  x: number,
  y: number,
): ChinaMapPlaceView | null {
  return (
    getChinaMapVisiblePlaces(provinces).find((view) => containsPoint(view.place, x, y)) ?? null
  );
}
